<script setup>
import { railsApi } from '@/utils/railsApi'

definePage({
  meta: {
    adminArea: true,
  },
})

const { t, te } = useI18n()
const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const logs = ref([])
const page = ref(1)
const hasNextPage = ref(false)

const trainerId = computed(() => route.query.trainer_id ? String(route.query.trainer_id) : '')

const formatWhen = value => {
  if (!value)
    return '—'

  return new Date(value).toLocaleString('nl-NL')
}

const actionLabel = action => {
  const key = `admin.actions.${action}`

  return te(key) ? t(key) : action
}

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    const params = new URLSearchParams()

    params.set('page', String(page.value))
    if (trainerId.value)
      params.set('trainer_id', trainerId.value)

    const data = await railsApi(`/api/v1/admin/audit_logs?${params}`)

    logs.value = data.audit_logs || []
    hasNextPage.value = !!data.has_next_page
  }
  catch (err) {
    error.value = err.payload?.error || err.message || t('admin.auditLogs.loadError')
  }
  finally {
    loading.value = false
  }
}

watch(() => route.query.trainer_id, () => {
  page.value = 1
  load()
})

onMounted(load)

const clearFilter = () => {
  router.replace({ name: 'admin-audit-logs' })
}

const prevPage = () => {
  if (page.value > 1) {
    page.value -= 1
    load()
  }
}

const nextPage = () => {
  if (hasNextPage.value) {
    page.value += 1
    load()
  }
}
</script>

<template>
  <div>
    <h1 class="text-h4 mb-4">
      {{ $t('admin.auditLogs.title') }}
    </h1>

    <p
      v-if="trainerId"
      class="text-medium-emphasis mb-4"
    >
      {{ $t('admin.auditLogs.filtered', { id: trainerId }) }}
      <VBtn
        variant="text"
        size="small"
        @click="clearFilter"
      >
        {{ $t('admin.auditLogs.clearFilter') }}
      </VBtn>
    </p>

    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ error }}
    </VAlert>

    <VCard>
      <VCardText>
        <VProgressLinear
          v-if="loading"
          indeterminate
          class="mb-4"
        />
        <p
          v-if="!loading && !logs.length"
          class="text-medium-emphasis mb-0"
        >
          {{ $t('admin.auditLogs.empty') }}
        </p>
        <VTable v-else-if="logs.length">
          <thead>
            <tr>
              <th scope="col">
                {{ $t('admin.columns.when') }}
              </th>
              <th scope="col">
                {{ $t('admin.columns.who') }}
              </th>
              <th scope="col">
                {{ $t('admin.columns.action') }}
              </th>
              <th scope="col">
                {{ $t('admin.columns.target') }}
              </th>
              <th scope="col">
                {{ $t('admin.columns.reason') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="log in logs"
              :key="log.id"
            >
              <td>{{ formatWhen(log.created_at) }}</td>
              <td>{{ log.platform_admin?.email }}</td>
              <td>{{ actionLabel(log.action) }}</td>
              <td>
                <RouterLink
                  v-if="log.auditable_type === 'Trainer'"
                  :to="{ name: 'admin-trainers-id', params: { id: log.auditable_id } }"
                >
                  {{ log.auditable_label }}
                </RouterLink>
                <span v-else>{{ log.auditable_label }}</span>
              </td>
              <td>{{ log.reason }}</td>
            </tr>
          </tbody>
        </VTable>

        <div
          v-if="logs.length"
          class="d-flex align-center gap-3 mt-4"
        >
          <VBtn
            variant="tonal"
            :disabled="page <= 1 || loading"
            @click="prevPage"
          >
            {{ $t('admin.auditLogs.previous') }}
          </VBtn>
          <span class="text-medium-emphasis">{{ $t('admin.auditLogs.page', { page }) }}</span>
          <VBtn
            variant="tonal"
            :disabled="!hasNextPage || loading"
            @click="nextPage"
          >
            {{ $t('admin.auditLogs.next') }}
          </VBtn>
        </div>
      </VCardText>
    </VCard>
  </div>
</template>
